import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Activity, Loader2 } from 'lucide-react';
import { config } from '@/lib/config';

const LEVELS = [
  { id: 'smallbiz', name: 'Small Business', price: '$400 / yr', blurb: 'For growing local businesses.' },
  { id: 'corporate', name: 'Corporate Partner', price: '$1,200 / yr', blurb: 'Maximum visibility + sponsorship perks.' },
  { id: 'nonprofit', name: 'Nonprofit', price: '$250 / yr', blurb: 'Discounted rate for 501(c)(3) orgs.' },
];

export function Join() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [level, setLevel] = useState('smallbiz');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${config.paymentsUrl}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, level, email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error ?? 'Could not start checkout');
      window.location.href = data.url; // redirect to Stripe Checkout
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/40">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-10 flex items-center gap-2 text-lg font-semibold tracking-tight">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground">
            <Activity className="h-5 w-5" />
          </span>
          Join the Chamber
        </div>

        <h1 className="text-4xl font-bold tracking-tight">Become a member today</h1>
        <p className="mt-3 max-w-xl text-muted-foreground">
          Pick a membership level and join in under a minute. Secure checkout by Stripe.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-8">
          <fieldset className="grid gap-4 sm:grid-cols-3">
            {LEVELS.map((l) => (
              <label
                key={l.id}
                className={`cursor-pointer rounded-xl border p-5 shadow-sm transition-colors ${
                  level === l.id ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40'
                }`}
              >
                <input type="radio" name="level" value={l.id} checked={level === l.id} onChange={() => setLevel(l.id)} className="sr-only" />
                <p className="font-semibold">{l.name}</p>
                <p className="mt-1 text-2xl font-bold text-primary">{l.price}</p>
                <p className="mt-2 text-sm text-muted-foreground">{l.blurb}</p>
              </label>
            ))}
          </fieldset>

          <div className="max-w-sm">
            <label htmlFor="email" className="text-sm font-medium">
              Work email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Redirecting to secure checkout…' : 'Continue to payment'}
          </button>
        </form>
      </div>
    </div>
  );
}
